import { Link } from 'react-router-dom';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ProjectCarousel from '../../../components/base/ProjectCarousel';
import VideoBackground from '../../../components/base/VideoBackground';

const SocialMediaContentPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 relative overflow-hidden">
      <VideoBackground />
      <Header />
      
      <main className="relative z-10">
        {/* Hero Section */}
        <section 
          className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(147, 51, 234, 0.8), rgba(236, 72, 153, 0.8)), url('https://public.readdy.ai/ai/img_res/38452c1a6b1250e5477fac0c827c51c1.jpg')`
          }}
        >
          <div className="container mx-auto px-6 text-center text-white">
            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-12 shadow-2xl border border-white/20">
              <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
                Social Media Content Marketing
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-gray-100 max-w-3xl mx-auto leading-relaxed">
                Transform your small business with premium social media content that generates leads, builds brand awareness, and drives real results. We create original, engaging content that makes your business swing to success!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/consultation"
                  className="bg-gradient-to-r from-yellow-400 to-orange-400 text-purple-800 px-8 py-4 rounded-full text-lg font-bold hover:from-yellow-300 hover:to-orange-300 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                >
                  Get Free Consultation
                </Link>
                <Link
                  to="/projects"
                  className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/30 transition-all duration-300 border border-white/30 whitespace-nowrap cursor-pointer"
                >
                  View Our Work
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-12 border border-purple-200">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                  Why Small Businesses Need Original Premium Content
                </h2>
                <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                  In today's digital landscape, generic content gets lost in the noise. Your small business deserves content that stands out, engages your audience, and converts followers into loyal customers.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center p-6 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-eye-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Stand Out From Competition</h3>
                  <p className="text-gray-600">Original content helps your brand cut through the clutter and capture your audience's attention in crowded social media feeds.</p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-pink-100 to-orange-100 rounded-2xl">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-heart-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Build Trust & Credibility</h3>
                  <p className="text-gray-600">Premium content establishes your expertise and builds trust with potential customers, leading to higher conversion rates.</p>
                </div>

                <div className="text-center p-6 bg-gradient-to-br from-orange-100 to-yellow-100 rounded-2xl">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-rocket-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Drive Real Results</h3>
                  <p className="text-gray-600">Strategic content marketing generates qualified leads, increases sales, and grows your business measurably.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 rounded-3xl shadow-2xl p-12 text-white">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  The Benefits of Content Marketing for Small Businesses
                </h2>
                <p className="text-xl text-purple-100 max-w-4xl mx-auto leading-relaxed">
                  Content marketing isn't just about posting pretty pictures. It's a strategic approach that delivers measurable results for your business growth.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                    <i className="ri-user-add-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Lead Generation</h3>
                  <p className="text-purple-100">Attract qualified prospects who are genuinely interested in your products or services.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                    <i className="ri-award-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Brand Authority</h3>
                  <p className="text-purple-100">Establish your business as the go-to expert in your industry through valuable content.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                    <i className="ri-line-chart-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Increased Sales</h3>
                  <p className="text-purple-100">Convert social media followers into paying customers with strategic content funnels.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
                    <i className="ri-money-dollar-circle-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Cost-Effective</h3>
                  <p className="text-purple-100">Get better ROI compared to traditional advertising while building long-term brand value.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Content Marketing Helps Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-12 border border-purple-200">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                  What Content Marketing Does to Help Small Businesses Grow
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=small%20business%20growth%20through%20social%20media%20marketing%2C%20business%20owner%20celebrating%20success%20with%20charts%20showing%20growth%2C%20bright%20colorful%20office%20environment%2C%20social%20media%20analytics%20on%20screens%2C%20professional%20marketing%20team%20working&width=600&height=400&seq=growth1&orientation=landscape"
                    alt="Business Growth Through Content Marketing"
                    className="w-full h-80 object-cover object-top rounded-2xl shadow-lg"
                  />
                </div>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-search-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Increases Online Visibility</h3>
                      <p className="text-gray-600">Strategic content improves your search rankings and social media reach, making it easier for customers to find you.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-purple-400 to-pink-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-group-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Builds Community</h3>
                      <p className="text-gray-600">Engaging content creates a loyal community around your brand, leading to repeat customers and referrals.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-orange-400 to-red-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-target-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Targets Ideal Customers</h3>
                      <p className="text-gray-600">Precise content targeting ensures you reach the right audience at the right time with the right message.</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full flex-shrink-0">
                      <i className="ri-trophy-line text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">Drives Measurable Results</h3>
                      <p className="text-gray-600">Track engagement, leads, and sales to see exactly how content marketing impacts your bottom line.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Social Swing Studio Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded-3xl shadow-2xl p-12 text-white">
              <div className="text-center mb-12">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Why Choose Social Swing Studio?
                </h2>
                <p className="text-xl text-purple-100 max-w-4xl mx-auto leading-relaxed">
                  We're not just another marketing agency. We're your dedicated partners in business growth, committed to delivering exceptional results that make your business swing to new heights.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-green-400 to-blue-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-shield-check-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">100% Customer Service Guarantee</h3>
                  <p className="text-purple-100">We stand behind our work with a complete satisfaction guarantee. Your success is our success.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-yellow-400 to-orange-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-chat-smile-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Free Consultation</h3>
                  <p className="text-purple-100">Get expert advice with no obligation. We'll analyze your current strategy and provide actionable recommendations.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-bar-chart-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Proven Results</h3>
                  <p className="text-purple-100">Our track record speaks for itself - we've helped hundreds of small businesses achieve remarkable growth.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-blue-400 to-purple-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-wallet-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Affordable Pricing</h3>
                  <p className="text-purple-100">Premium quality doesn't have to break the bank. Our packages are designed for small business budgets.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-green-400 to-teal-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Lead Generation Focus</h3>
                  <p className="text-purple-100">Every piece of content is strategically designed to attract and convert your ideal customers.</p>
                </div>

                <div className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20">
                  <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-r from-red-400 to-pink-500 text-white rounded-full mx-auto mb-4">
                    <i className="ri-rocket-2-line text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-bold mb-3">Growth-Oriented</h3>
                  <p className="text-purple-100">We're obsessed with helping small businesses grow and achieve their full potential in the digital space.</p>
                </div>
              </div>

              <div className="text-center">
                <Link
                  to="/consultation"
                  className="bg-white text-purple-600 px-8 py-4 rounded-full text-lg font-bold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-block"
                >
                  Start Your Free Consultation Today
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Recent Projects Portfolio Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-6">
                Recent Projects & Success Stories
              </h2>
              <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
                See how we've helped small businesses like yours achieve remarkable growth through strategic content marketing. These real results speak for themselves.
              </p>
            </div>

            <ProjectCarousel />

            <div className="text-center mt-12">
              <Link
                to="/projects"
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full text-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer inline-block"
              >
                View All Projects
              </Link>
            </div>
          </div>
        </section>

        {/* Compact Pricing Section */}
        <section className="py-16 relative">
          <div className="container mx-auto px-6">
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-purple-200">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
                  Social Media Content Packages
                </h2>
                <p className="text-lg text-gray-700 max-w-3xl mx-auto">
                  Choose the perfect package to grow your business with professional social media content marketing.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-6 relative">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Starter</h3>
                    <div className="text-3xl font-bold text-purple-600 mb-1">$299</div>
                    <div className="text-sm text-gray-600">per month</div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 mb-6">
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>8 Posts per month</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>2 Platforms</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Content Creation</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Basic Analytics</li>
                  </ul>
                  <Link
                    to="/consultation"
                    className="block w-full bg-purple-600 text-white py-2 rounded-full text-center font-semibold hover:bg-purple-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </div>

                <div className="bg-gradient-to-br from-pink-100 to-orange-100 rounded-2xl p-6 relative border-2 border-pink-400">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-pink-500 text-white px-4 py-1 rounded-full text-xs font-bold">Most Popular</span>
                  </div>
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Growth</h3>
                    <div className="text-3xl font-bold text-pink-600 mb-1">$599</div>
                    <div className="text-sm text-gray-600">per month</div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 mb-6">
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>16 Posts per month</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>3 Platforms</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Premium Content</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Community Management</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Monthly Analytics</li>
                  </ul>
                  <Link
                    to="/consultation"
                    className="block w-full bg-pink-600 text-white py-2 rounded-full text-center font-semibold hover:bg-pink-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </div>

                <div className="bg-gradient-to-br from-orange-100 to-yellow-100 rounded-2xl p-6 relative">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Premium</h3>
                    <div className="text-3xl font-bold text-orange-600 mb-1">$999</div>
                    <div className="text-sm text-gray-600">per month</div>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700 mb-6">
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>24 Posts per month</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>All Platforms</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Video Content</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Paid Ad Management</li>
                    <li className="flex items-center"><i className="ri-check-line text-green-500 mr-2"></i>Weekly Reports</li>
                  </ul>
                  <Link
                    to="/consultation"
                    className="block w-full bg-orange-600 text-white py-2 rounded-full text-center font-semibold hover:bg-orange-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Get Started
                  </Link>
                </div>
              </div>

              <div className="text-center mt-8">
                <Link
                  to="/prices"
                  className="text-purple-600 hover:text-purple-700 font-semibold cursor-pointer"
                >
                  View All Pricing Options →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div 
              className="relative bg-cover bg-center rounded-3xl shadow-2xl overflow-hidden"
              style={{
                backgroundImage: `linear-gradient(rgba(147, 51, 234, 0.9), rgba(236, 72, 153, 0.9)), url('https://readdy.ai/api/search-image?query=successful%20small%20business%20owner%20celebrating%20growth%20with%20social%20media%20marketing%20results%2C%20bright%20colorful%20office%20environment%2C%20charts%20showing%20business%20success%2C%20professional%20marketing%20consultation%20meeting%2C%20vibrant%20atmosphere&width=1200&height=600&seq=cta1&orientation=landscape')`
              }}
            >
              <div className="p-12 text-center text-white">
                <h2 className="text-4xl md:text-5xl font-bold mb-6">
                  Ready to Swing Your Business to Success?
                </h2>
                <p className="text-xl text-purple-100 max-w-3xl mx-auto mb-8 leading-relaxed">
                  Don't let your competitors get ahead. Start your content marketing journey today with a free, no-obligation consultation. Let's discuss how we can help your business grow!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/consultation"
                    className="bg-gradient-to-r from-yellow-400 to-orange-400 text-purple-800 px-8 py-4 rounded-full text-lg font-bold hover:from-yellow-300 hover:to-orange-300 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
                  >
                    Book Free Consultation
                  </Link>
                  <Link
                    to="/contact"
                    className="bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-full text-lg font-bold hover:bg-white/30 transition-all duration-300 border border-white/30 whitespace-nowrap cursor-pointer"
                  >
                    Contact Us Now
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SocialMediaContentPage;